import { useIntl } from '@weenat/wintl';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useClient, useQuery } from '../..';
import { Brand, DeviceCategory } from '../../enums/Devices';
import { filterableDeviceFaultEvents, getDeviceEventName } from './events';
const devicesMetrics = [
    'T',
    'RR',
    'U',
    'T_DRY',
    'T_WET',
    'U_CAPA',
    'HPOT',
    'T_SOIL',
    'FF',
    'DD',
    'LW_D'
];
const EMPTY_MODELS = [];
function useDeviceFilteringOptions({ fetchModels, orgId }) {
    const { t } = useIntl();
    const client = useClient();
    const modelsRequest = useQuery(client.devices.getAvailableModels({ organizationId: orgId }), {
        enabled: fetchModels && !isNil(orgId)
    });
    const models = modelsRequest.data ?? EMPTY_MODELS;
    const ownershipOptions = useMemo(() => [
        {
            id: 'all-devices',
            label: t('models.device.filters.all_devices', { capitalize: true }),
            value: undefined
        },
        {
            id: 'current-org-devices',
            label: t('models.device.filters.org_devices', { capitalize: true }),
            value: orgId
        }
    ], [orgId, t]);
    const brandOptions = useMemo(() => [
        {
            id: 'weenat',
            label: t('summaries.metric_providers.weenat', { capitalize: true }),
            value: Brand.weenat
        },
        {
            id: 'pessl',
            label: t('summaries.metric_providers.pessl', { capitalize: true }),
            value: Brand.pessl
        },
        {
            id: 'davis',
            label: t('summaries.metric_providers.davis', { capitalize: true }),
            value: Brand.davis
        },
        {
            id: 'corhize',
            label: t('summaries.metric_providers.corhize', { capitalize: true }),
            value: Brand.corhize
        }
    ], [t]);
    const metricsOptions = useMemo(() => devicesMetrics.map((m) => {
        const key = `metrics.${m}`;
        return {
            id: m,
            label: t(key, { capitalize: true }),
            value: m
        };
    }), [t]);
    const faultEventsOptions = useMemo(() => [
        {
            id: 'all-events',
            label: t('models.device.filters.current_event', { capitalize: true }),
            value: filterableDeviceFaultEvents
        },
        ...filterableDeviceFaultEvents.map((evt) => {
            const label = getDeviceEventName(evt, t);
            return {
                id: evt.toString(),
                label,
                value: evt
            };
        })
    ], [t]);
    const modelOptions = useMemo(() => models.map((model) => {
        return {
            id: model.toString(),
            label: model,
            value: model
        };
    }), [models]);
    const categoryOptions = useMemo(() => [
        {
            id: 'agro-weather-category',
            label: t('models.agro_weather_device.model.name', { capitalize: true }),
            value: DeviceCategory.agroWeather
        },
        {
            id: 'irrigation-probes-category',
            label: t('models.irrigation_probe.model.name', { capitalize: true }),
            value: DeviceCategory.irrigationProbes
        }
    ], [t]);
    const activeLinksOptions = useMemo(() => [
        {
            id: 'with-active-links',
            label: t('actions.answer_yes', { capitalize: true }),
            value: true
        },
        {
            id: 'without-active-links',
            label: t('actions.answer_no', { capitalize: true }),
            value: false
        }
    ], [t]);
    return {
        brandOptions,
        faultEventsOptions,
        metricsOptions,
        modelOptions,
        ownershipOptions,
        categoryOptions,
        activeLinksOptions,
        request: modelsRequest
    };
}
export default useDeviceFilteringOptions;
